/*
 * The wizard's steps, by name and in order (`twin-details-page-shell`, R1). Kept out of
 * `OnboardingPage.tsx` so a small module (Versions' text rules, `twin-details-page-versions`) can
 * read the step titles without importing the whole page.
 */

import type { IconName } from '@repo/design-system';
import { NO_SCENE, WASHED_SCENE } from '../../components/3D/planView/PlanStageTunnel';
import type { StageScene } from '../../components/3D/planView/PlanStageTunnel';

/** Every step by name. Launch is last and is not in `STEPS`. */
export type StepId =
  | 'twinDetails'
  | 'openingHours'
  | 'assets'
  | 'plan'
  | 'cameras'
  | 'observers'
  | 'compositeMeasures'
  | 'positionOnMap'
  | 'launch';

/**
 * The pre-Launch steps, in order — **the only place the order lives** (`twin-details-page-shell`,
 * R1). The step list, the step titles and *Next* all read it, so a step is added or moved by
 * editing this list and nothing else is renumbered.
 */
export const STEPS = [
  { id: 'twinDetails', title: 'Twin details' },
  // Opening hours (`twin-details-page-hours-week`, frame 06): saved to the twin straight away.
  { id: 'openingHours', title: 'Opening hours' },
  { id: 'assets', title: 'Equipment/Assets' },
  { id: 'plan', title: 'Floor Plan' },
  { id: 'cameras', title: 'Camera Feeds' },
  { id: 'observers', title: 'Observers' },
  { id: 'compositeMeasures', title: 'Composite measures' },
  /*
   * A placeholder until the 3D plan (`twin-details-page-position-placeholder`, frame 10): it holds
   * its place now so the order never changes when the second epic replaces it. Never blocks Launch.
   */
  // Position twin (`onboarding-persistent-scene-position-twin`, R1.1): renamed from *Position on map*;
  // its id stays, a rename of the id being a mechanical change of its own.
  { id: 'positionOnMap', title: 'Position twin' },
] as const satisfies readonly { id: Exclude<StepId, 'launch'>; title: string }[];

/**
 * The steps whose marker is a glyph rather than a number (`twin-details-page-shell`, R2.1): Launch's
 * rocket. Position twin draws its number, 8, since `onboarding-persistent-scene-position-twin` (R1.2).
 * Here, beside the order, so the wizard's step list and create mode's locked list
 * (`createModeNav.ts`) draw the same markers.
 */
export const STEP_ICONS: Readonly<Partial<Record<StepId, IconName>>> = {
  launch: 'rocket',
};

/**
 * Each step's scene on the onboarding stage (`spec-onboarding-persistent-scene-stage`, R3): the
 * camera, the underlay and whether the map takes gestures. The only place a step's scene is named:
 * the stage itself names no step. Position twin shows the map on the page backdrop, interactive. The
 * five non-plan steps show the plan washed on the page backdrop, not interactive (`WASHED_SCENE`,
 * `spec-onboarding-persistent-scene-wash`): the page washes it with the frosted `Scrim` once it has
 * drawn. Floor Plan, Camera Feeds and Observers draw maps of their own and show none of the stage's.
 */
export const STEP_SCENES: Readonly<Record<StepId, StageScene>> = {
  twinDetails: WASHED_SCENE,
  openingHours: WASHED_SCENE,
  assets: WASHED_SCENE,
  plan: NO_SCENE,
  cameras: NO_SCENE,
  observers: NO_SCENE,
  compositeMeasures: WASHED_SCENE,
  // Position twin (`onboarding-persistent-scene-position-twin`, R2.1): the stage's map on the page's
  // backdrop, interactive, under the glass header, the step list and the step's floating panel.
  positionOnMap: { camera: 'plan-view', underlay: 'clear', interactive: true, placement: 'page' },
  launch: WASHED_SCENE,
};

/** The three editor steps, whose own plan maps a step change hands over between. */
const EDITOR_PLAN_STEPS: ReadonlySet<StepId> = new Set<StepId>(['plan', 'cameras', 'observers']);

/**
 * Does `step` draw a plan map (`spec-onboarding-persistent-scene-handover`, R2.1): one of the three
 * editor steps, or a step whose scene shows the stage's map. A washed step draws one only while the
 * washed plan is offered (`spec-onboarding-persistent-scene-wash`, R6.1): a twin with no stored
 * position or no plan has no map there to join, so its moves still cut.
 */
const drawsPlanMap = (step: StepId, washedPlan: boolean): boolean => {
  if (EDITOR_PLAN_STEPS.has(step)) return true;
  const { underlay } = STEP_SCENES[step];
  return underlay === 'washed' ? washedPlan : underlay !== 'none';
};

/**
 * R2.1, R2.2: is a change from `from` to `to` handed over? Only when one side is an editor step and
 * both draw a plan map. `washedPlan`: whether the washed plan is offered now (`washedPlanAvailable`).
 * Every other change is today's instant swap (ruled 2026-10-31).
 */
export const handsOver = (
  from: StepId,
  to: StepId,
  { washedPlan = false }: { washedPlan?: boolean } = {},
): boolean =>
  from !== to &&
  (EDITOR_PLAN_STEPS.has(from) || EDITOR_PLAN_STEPS.has(to)) &&
  drawsPlanMap(from, washedPlan) &&
  drawsPlanMap(to, washedPlan);

/**
 * The steps whose panels fade into one another (`onboarding-persistent-scene-panel-fades`, R2.1): the
 * steps the wash covers, and Position on map. Floor Plan, Camera Feeds and Observers keep maps of
 * their own, so a join that touches one cuts until the handover dissolves it.
 */
const FADING_STEPS: ReadonlySet<StepId> = new Set<StepId>([
  'twinDetails',
  'openingHours',
  'assets',
  'compositeMeasures',
  'positionOnMap',
  'launch',
]);

/**
 * Whether the move from `from` to `to` fades rather than cuts: both among the fading steps, and
 * different. `null` (the landing, or no step before) cuts.
 */
export const stepJoinFades = (from: StepId | null, to: StepId | null): boolean =>
  from !== null && to !== null && from !== to && FADING_STEPS.has(from) && FADING_STEPS.has(to);
